<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.expenseTypes',
    path: 'expense-types',
    component: 'ExpenseTypes',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ExpenseTypes',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const objectType = ref('expense-type');
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'code',
      header: $fn.value.gettext('Code'),
      width: '160px',
      required: true,
    },
    {
      name: 'name',
      header: $fn.value.gettext('Name'),
      width: '140px',
      required: true,
    },
    {
      name: 'description',
      header: $fn.value.gettext('Description'),
      width: '320px',
    },
  ]);

  const $data = computed(() => ({
    objectType: objectType.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('list_object_by_type', {
      parameters: { object_type: objectType.value },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'objectType' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      objectType,
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_efb06yswv_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_ckvduyi69_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .upsert({ ...$event.data.row, object_type: objectType.value })
    );
  };
  const act_3343864469950044_script_ckvduyi69_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_ckvduyi69_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_p1de6yj0i_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .delete({ ...$event.data.row, object_type: objectType.value })
    );
  };
  const act_3343864469950044_script_p1de6yj0i_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_p1de6yj0i_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_efb06yswv =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_efb06yswv_getActionArgs,
    });
  const inst_evh_7315092382398562_close_2iraq9v0g =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_efb06yswv,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_2iraq9v0g],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_xaexuao6t =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_vrfaw7e0p =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_xaexuao6t,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_vrfaw7e0p],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_2kgjt2f1e =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_ppgsq6rfa =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_2kgjt2f1e,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_ppgsq6rfa],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_ckvduyi69 = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_ckvduyi69_getActionArgs,
  });
  const inst_evh_3343864469950044_save_5ii79iecf =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_ckvduyi69,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_5ii79iecf],
    event: 'save',
  });
  const act_3343864469950044_script_p1de6yj0i = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_p1de6yj0i_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_rbtbtek6e =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_p1de6yj0i,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_rbtbtek6e],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_2iraq9v0g,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_vrfaw7e0p,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_ppgsq6rfa,
    reload: inst_evh_8320016629450276_clickPreventStop_ppgsq6rfa,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_5ii79iecf,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_rbtbtek6e,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_2iraq9v0g = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_2iraq9v0g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_vrfaw7e0p = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_vrfaw7e0p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_ppgsq6rfa = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_ppgsq6rfa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_5ii79iecf = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_5ii79iecf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_rbtbtek6e = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_rbtbtek6e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ExpenseTypes"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ gettext('Expense types') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.ExpenseTypes"
        editable
        :isRowEditable="(p) => !p.locked"
        :value="items"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
